import { useContext, useEffect, useRef, useState } from 'react';
import { SmartContext } from '../../../../library/Core/SmartContext';
import { axiosClient } from '../../../../library/Service/axiosClient';
import { ACTIVITY_CATEGORIES, getActivityCategoryLabel } from '../../../management/alumni-list/activityCategories';

const PAGE_SIZE = 25;

const ActivityMarking = () => {
    const { state } = useContext(SmartContext);

    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [selectedMode, setSelectedMode] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [activityLogs, setActivityLogs] = useState<any[]>([]);
    const [activityCount, setActivityCount] = useState<number | undefined>(undefined);
    const [clicked, setClicked] = useState<boolean>(false);
    const [opened, setOpened] = useState<boolean>(false);
    const [platform, setPlatform] = useState<string>('');

    // Lazy loading state
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const pageRef = useRef(1);
    const sentinelRef = useRef<HTMLDivElement>(null);
    // Tags each fetch so a slow/late response from a superseded filter set is ignored.
    const requestIdRef = useRef(0);
    // Keep latest load-more function accessible inside the observer callback without stale closure
    const loadMoreFnRef = useRef<() => void>(() => {});

    const buildPayload = (pageNum: number) => ({
        id: state?.data?.id,
        status: selectedStatus,
        startDate: startDate,
        endDate: endDate,
        mode: selectedMode,
        category: selectedCategory,
        clicked: clicked,
        opened: opened,
        platform: platform,
        page: pageNum,
    });

    const fetchPage = async (pageNum: number, reset: boolean) => {
        if (reset) setIsLoading(true);
        else setIsFetchingMore(true);

        const payload = buildPayload(pageNum);
        const requestId = ++requestIdRef.current;

        // The count query scans the whole result set independently so it never blocks the
        // table render — the header shows the count once it lands.
        if (reset) {
            setActivityCount(undefined);
            axiosClient()
                .post(`${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/search/activity-making-count`, payload)
                .then((countRes) => {
                    if (requestId === requestIdRef.current && countRes?.status === 200) {
                        setActivityCount(countRes.data[0]?.count ?? 0);
                    }
                })
                .catch(() => { if (requestId === requestIdRef.current) setActivityCount(0); });
        }

        try {
            const listRes = await axiosClient().post(
                `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/search/activity-making-list`,
                payload
            );

            // A newer fetch superseded this one — drop the stale response.
            if (requestId !== requestIdRef.current) return;

            if (listRes.status === 200) {
                const newLogs: any[] = listRes.data || [];
                setActivityLogs(prev => (reset ? newLogs : [...prev, ...newLogs]));
                setHasMore(newLogs.length === PAGE_SIZE);
                pageRef.current = pageNum;
            }
        } catch (error) {
            console.error('Error fetching ActivityLogs:', error);
            if (requestId === requestIdRef.current && reset) { setActivityLogs([]); setActivityCount(0); }
        } finally {
            setIsLoading(false);
            if (requestId === requestIdRef.current) setIsFetchingMore(false);
        }
    };

    // Refresh the load-more function every render so the observer always has fresh state
    loadMoreFnRef.current = () => {
        if (!hasMore || isFetchingMore || isLoading) return;
        fetchPage(pageRef.current + 1, false);
    };

    // On filter change: reset to page 1
    useEffect(() => {
        pageRef.current = 1;
        setHasMore(true);
        fetchPage(1, true);
    }, [selectedStatus, startDate, endDate, selectedMode, selectedCategory, clicked, opened, platform]);

    // IntersectionObserver — set up once, always calls the latest loadMoreFnRef
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMoreFnRef.current();
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    function formatDate(dateString: string) {
        const date = new Date(dateString);
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    }

    // Updated renderDescription function
    const renderDescription = (activityLog: any) => {
        // EMAIL / SENDGRID type – keep as plain text
        if (['1', '2'].includes(activityLog?.platformType)) {
            if (!(activityLog?.clicksCount || activityLog?.opensCount)) {
                return `"${activityLog?.subject}"`;
            }
            const prefix = activityLog?.clicksCount ? 'Alumnus clicked Email with subject "' : 'Alumnus opened Email with subject "';
            return `${prefix}${activityLog?.subject}"`;
        }

        // PLATFORM TYPES 3–6 – subject may contain HTML (your <a> tag)
        if (['3', '4', '5', '6'].includes(activityLog?.platformType)) {
            return <span dangerouslySetInnerHTML={{ __html: activityLog?.subject || '' }} />;
        }

        // Fallback
        return activityLog?.subject;
    };

    return (
        <div className="tab-pane fade active show" id="pills-activity-marking" role="tabpanel" aria-labelledby="pills-underGraduate-tab">
            <div className="studentInteractions_wrap">
                <div className="row g-2 mb-3 align-items-center mt-3">
                    <div className="col-sm">
                        <div className="d-flex flex-column">
                            <h3 data-aos="fade-left" data-aos-delay="600" className="font-24 font-600 mb-0 block-title aos-init aos-animate">
                                Activity Log
                            </h3>
                        </div>
                    </div>
                </div>
                <div className="d-flex flex-wrap justify-content-between align-items-start align-items-md-center mb-2 filterSearch">
                    <div className="d-flex flex-wrap flex-1 mb-0 justify-content-start">
                        <button
                            className="btn-grpfiter me-3"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseOne"
                            aria-expanded="true"
                            aria-controls="collapseOne">
                            <i className="icon-filter font-13 me-2"></i>
                            Filters
                        </button>
                    </div>
                </div>
                <div className="fitergroup mb-3">
                    <div id="collapseOne" className="accordion-collapse collapse show" aria-labelledby="headingOne">
                        <div className="accordion-body p-4">
                            <form className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Activity Date</label>
                                    <div className="datewrap">
                                        <div className="from">
                                            <input
                                                onChange={(e) => setStartDate(e.target.value)}
                                                value={startDate}
                                                type="date"
                                                className="form-control"
                                            />
                                        </div>
                                        <div className="arrow">
                                            <span className="icon-right"></span>
                                        </div>
                                        <div className="to">
                                            <input
                                                onChange={(e) => setEndDate(e.target.value)}
                                                value={endDate}
                                                type="date"
                                                className="form-control"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-3 col-sm-6">
                                    <label className="form-label">Mode</label>
                                    <select
                                        className="form-select"
                                        onChange={(e) => setSelectedMode(e.target.value)}>
                                        <option value="">Mode</option>
                                        <option value="EMAIL">Email</option>
                                        <option value="WHATSAPP">Whatsapp</option>
                                        <option value="PHONE_CALL">Phone Call</option>
                                        <option value="PHYSICAL_EVENT">Physical Event</option>
                                        <option value="ONLINE_EVENT">Online Event</option>
                                        <option value="CAMPUS_VISIT">Campus Visit</option>
                                        <option value="SYSTEM">System</option>
                                        <option value="OTHERS">Others</option>
                                    </select>
                                </div>
                                <div className="col-md-3 col-sm-6">
                                    <label className="form-label">Category</label>
                                    <select
                                        className="form-select"
                                        onChange={(e) => setSelectedCategory(e.target.value)}>
                                        <option value="">Category</option>
                                        {ACTIVITY_CATEGORIES.map(({ code, label }) => (
                                            <option key={code} value={code}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="table-responsive attendee_dtl">
                    <div className="mb-2 font-14 text-muted">
                        {isLoading
                            ? 'Loading…'
                            : `Showing ${activityLogs.length}${activityCount !== undefined ? ` of ${activityCount}` : ''} records`}
                    </div>

                    <table className="table" style={{ minWidth: '958px', width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={{ width: '150px' }}>Activity Date</th>
                                <th style={{ width: '200px' }}>Purpose</th>
                                <th style={{ width: '450px' }}>Description</th>
                                <th style={{ width: '150px' }}>Tracked By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={4} className="text-center py-4">
                                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                                        Loading records…
                                    </td>
                                </tr>
                            ) : activityLogs.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="text-center py-4 text-muted">No records found.</td>
                                </tr>
                            ) : (
                                activityLogs.map((activityLog: any, idx: number) => (
                                    <tr key={activityLog?.id || activityLog?.uuid || idx}>
                                        <td>{formatDate(activityLog?.lastEventTime)}</td>
                                        <td>
                                            <div className="d-flex flex-column align-items-start lbl_orange font-14">
                                                <div><span className="font-500">Category :</span> {getActivityCategoryLabel(activityLog?.category)}</div>
                                                <div><span className="font-500">Mode :</span> {activityLog?.mode?.split('_').join(' ')}</div>
                                            </div>
                                        </td>
                                        <td>{renderDescription(activityLog)}</td>
                                        <td>
                                            <div className="d-flex flex-column align-items-start lbl_orange font-14">
                                                <div className="font-500">System</div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {/* Infinite scroll sentinel */}
                    <div ref={sentinelRef} style={{ height: '1px' }} />
                    {isFetchingMore && (
                        <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                            Loading more…
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ActivityMarking;
